
import { supabase } from '@/integrations/supabase/client';
import { ContentType } from '@/services/comments/types';

/**
 * Fetch the updated comment count for a content item
 * Including both top-level comments and replies
 * @param contentId The ID of the content item
 * @param contentType The type of content (poll or card)
 * @param parentId Optional parent ID for counting only replies to a specific comment
 * @returns The updated count
 */
export async function fetchUpdatedCommentCount(
  contentId: string, 
  contentType: ContentType,
  parentId: string | null = null
): Promise<number> {
  // Determine which table to query based on content type
  const table = contentType === 'poll' ? 'poll_comments' : 'card_comments';
  const idField = contentType === 'poll' ? 'poll_id' : 'card_id';
  
  try {
    // Use explicit QueryBuilder types to avoid deep recursion
    const query = supabase
      .from(table)
      .select('*', { count: 'exact', head: true })
      .is('deleted', false);
      
    // If we're counting replies for a specific comment
    if (parentId) {
      return countWithParent(query, parentId);
    } else {
      // If we're counting ALL comments for a content item
      return countAllForContent(query, idField, contentId);
    }
  } catch (error) {
    console.error(`Error fetching comment count:`, error);
    return 0;
  }
}

// Helper function to count comments with a specific parent
async function countWithParent(query: any, parentId: string): Promise<number> {
  try {
    const { count, error } = await query.eq('parent_id', parentId);
    
    if (error) {
      console.error('Error counting replies:', error);
      return 0;
    }
    
    return count !== null ? Number(count) : 0;
  } catch (err) {
    console.error('Error in countWithParent:', err);
    return 0;
  }
}

// Helper function to count all comments for a content item
async function countAllForContent(query: any, idField: string, contentId: string): Promise<number> {
  try {
    const { count, error } = await query.eq(idField, contentId);
    
    if (error) {
      console.error('Error counting comments:', error);
      return 0;
    }
    
    const finalCount = count !== null ? Number(count) : 0;
    console.log(`Comment count: ${finalCount}`);
    return finalCount;
  } catch (err) {
    console.error('Error in countAllForContent:', err);
    return 0;
  }
}
